import { useState, useEffect, useCallback } from 'react'
import { getCurrentItemSnapshot, getBodyAsync, getAttachments, onItemChanged } from '@/lib/office'
import type { LinkableEmail } from '@/types'

interface UseCurrentItemResult {
  item: LinkableEmail | null
  isLoading: boolean
  reload: () => void
}

export function useCurrentItem(): UseCurrentItemResult {
  const [item, setItem] = useState<LinkableEmail | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const load = useCallback(async () => {
    setIsLoading(true)

    const snapshot = getCurrentItemSnapshot()
    if (!snapshot) {
      setItem(null)
      setIsLoading(false)
      return
    }

    // Fetch body and attachments in parallel
    const [bodyHtml, bodyText, attachments] = await Promise.all([
      getBodyAsync('html'),
      getBodyAsync('text'),
      Promise.resolve(getAttachments()),
    ])

    setItem({
      ...snapshot,
      bodyHtml,
      bodyText,
      attachments,
    })
    setIsLoading(false)
  }, [])

  useEffect(() => {
    void load()
    // Re-load when the user selects a different email
    const unsubscribe = onItemChanged(() => { void load() })
    return unsubscribe
  }, [load])

  return { item, isLoading, reload: load }
}
